import {ChartAggOption} from '../../../generated/graphql';
import {ColorPaletteOption} from '../../../util/colors';
import {getDatadogRum} from '../../../util/datadog/datadogRum';
import {LineProperties} from '../../../util/plotHelpers/types';
import {LineConfig} from '../config/types';
import {BucketedLine} from '../types';
import {ExcludeOutliersValues} from './../../../components/WorkspaceDrawer/Settings/types';
import {BIN_POINT_TO_USE} from './../../../util/plotHelpers/chartTypes';
import {Key} from './../../../util/runTypes';
import {parseHistory} from './../componentOutliers/parseHistory';
import {PointWithMeta} from './buildPoints';
import {
  convertLineTemplateToLine,
  convertRunToLineTemplate,
  ConvertRunToLineTemplateParams,
  LineTemplate,
} from './convertLines';
import {addSmoothedLineFromBackend} from './lineMutations';
import {BackendLine, HistoryRecord, LineAggregation, SafePoint} from './types';
import {getSafeYKey} from './util';

export type UseLineBehavior = {
  canLogVitals: boolean;
  usingExpressions: boolean;
  usesTimeConversion: boolean;
  useBackendSmoothedLineIfPresent: boolean;
  useBackendLineData: boolean;
};

/**
 * Calculate line opacity based on the estimated number of lines to be rendered.
 * The problem is that thin spikes are hard to detect (so higher opacity), but fat
 * ranges overwhelm the chart with too much visual density. This is preliminary PR
 * to just solve the worst edge case in advance of a better solution:
 * https://weightsandbiases.slack.com/archives/C07JHLX0HJ5/p1760035726213469
 */
function calculateOpacity(lineCount: number): number {
  if (lineCount < 2) {
    return 0.375;
  }

  if (lineCount < 4) {
    return 0.3;
  }

  if (lineCount < 6) {
    return 0.25;
  }

  return 0.2;
}

/**
 * Creates a composite key for line data lookup.
 * Format: `${name}-${runsetId}` when runsetId exists, otherwise just `${name}`.
 * This keeps lines from different runsets separate while handling undefined runsetId gracefully.
 * This must match the key format used in lineDataByKey creation.
 */
export function createLineDataKey(
  runsetId: string | undefined,
  name: string
): string {
  return runsetId ? `${name}-${runsetId}` : name;
}

// Helper function with error handling for line data lookup
function getLineDataByKey(
  lineDataByKey: Record<string, LineTemplate>,
  runsetId: string | undefined,
  name: string
) {
  const key = createLineDataKey(runsetId, name);
  const matchedLine = lineDataByKey[key];
  if (!matchedLine) {
    throw new Error(
      `No line found for key: ${key} (runsetId: ${runsetId}, name: ${name})`
    );
  }
  return matchedLine;
}
/**
 * Note: the `minMax` agg value isn't really used, all we really do is check if it's the Avg or not, otherwise we return aux lines
 */
const aggregations = ['Avg', 'minMax'];

const DD_RUM = getDatadogRum();

export function buildLines({
  behavior,
  colorPaletteOption,
  data,
  debugMode,
  excludeOutliers,
  getGroupKeysByRunsetId,
  groupAgg,
  isRunsetGrouped,
  lineConfig,
  panelId,
  xMetrics,
  yMetrics,
}: {
  behavior: any;
  colorPaletteOption: ColorPaletteOption;
  data: BucketedLine[];
  debugMode: boolean;
  excludeOutliers?: ExcludeOutliersValues;
  getGroupKeysByRunsetId: (runsetId: string | undefined) => Key[];
  groupAgg?: ChartAggOption;
  isRunsetGrouped: (runsetId: string | undefined) => boolean;
  lineConfig: ConvertRunToLineTemplateParams['lineConfig'] &
    Pick<LineConfig, 'runSets' | 'xAxis'>;
  panelId?: string;
  xMetrics: string[];
  yMetrics: string[];
}) {
  /**
   * Calculate opacity based on estimated number of lines to be rendered.
   * This accounts for runs, metrics, and aggregations (Avg + minMax).
   */
  const estimatedLineCount = data.length * yMetrics.length;
  const lineOpacity = calculateOpacity(estimatedLineCount);

  /**
   * Hack to store non-finites (NF) points for each run and metric.
   *
   * The problem is that once you get into Graph/LinePlot/LinePlotPlot there's a lot of manipulation of the line data for [reasons]. The effect of this is that NanPoints that come in on aux lines (minMax areas) get handled differently and normalizing all the paths is tough. This solution is just to let both types of agg lines write to the same NF list, which is then only attached to the primary line. This way we can just check the primary line for NFs and handle them accordingly, and since the aux lines kick out NFs as x/y pairs (a NF point with x:5, y:-Infinity, y0: Infinity will spit out two NF points at 5/-Inf and 5/Inf) there's no type discrepancy with primary lines (which are just x/y points)
   */
  const nfPointsByRunAggMetric: {
    [name: string]: {
      [metric: string]: PointWithMeta[];
    };
  } = {};

  /**
   * We used to do this further down while we were computing the history data as well. This simplifies that by creating "templates" which are the basic data structures that we'll populate later.
   *
   * IMPORTANT: We key by `runsetId-name` instead of just `name` because:
   * 1. Grouped runs from different runsets can have the same name (e.g., "sweep:")
   * 2. Keying by name only would cause one runset's data to overwrite another's
   */
  const lineDataByKey = data.reduce(
    function constructLineDataByKey(acc, run) {
      const numRunsets = lineConfig.runSets.length;
      const matchedRunset = lineConfig.runSets.find(
        rs => rs.id === run.runsetId
      );
      // Only fall back to first runset if there's exactly one (unambiguous).
      // With multiple runsets, falling back would cause wrong legend/color.
      const runsetInfo =
        matchedRunset ?? (numRunsets === 1 ? lineConfig.runSets[0] : undefined);

      if (!matchedRunset && numRunsets > 1) {
        // Log error when we can't find a matching runset in multi-runset scenarios
        DD_RUM.addError(new Error('buildLines: runsetId mismatch'), {
          runsetId: run.runsetId,
          runName: run.name,
          availableRunsetIds: lineConfig.runSets.map(rs => rs.id),
        });
      }

      const runWithRunsetInfo = {
        ...run,
        runsetInfo,
      };

      // Key by runsetId + name to keep lines from different runsets separate
      const key = createLineDataKey(run.runsetId, run.name);
      acc[key] = convertRunToLineTemplate({
        colorPaletteOption,
        excludeOutliers: excludeOutliers,
        groupKeys: getGroupKeysByRunsetId(run.runsetId),
        lineConfig,
        lineOpacity,
        run: runWithRunsetInfo,
      });
      return acc;
    },
    {} as Record<string, LineTemplate>
  );

  /**
   * The goal in moving this code around is that we want to split apart the logic of creating lines from the logic of creating the history (the data and nanPoints). This is because we want to gradually move to being able to compute these histories on the backend, and then be able to easily switch between the two. It would be nice to do that in a single place abstracted from all the complexity of building the line metadata.
   *
   * The other reason to compute the lines up top is that the rules for when we show certain lines (and thus incur the cost of computing them) can sometimes be known in advance. In the following cases we can safely remove lines:
   * [TODO: the following aren't done but could be]
   * - if it's an original line and smoothing is on and we're not showing originals
   * - if we are excluding outliers (the minMax regions) and thus we don't need the aux lines
   */
  const lineSeeds = (aggregations as Array<'Avg' | 'minMax'>).flatMap(
    function buildLineSeedByAgg(type) {
      return data.flatMap(function buildLineSeedByRun(run) {
        nfPointsByRunAggMetric[run.name] = {};

        return yMetrics.map((metric, metricIndex) => {
          /**
           * Only one history list in the run will have the metric we're looking for since each metric gets broken out individually
           */

          nfPointsByRunAggMetric[run.name][metric] = [];

          const matchedHistory =
            run.history.find(function matchHistoryByKey(history) {
              if (!history[0]) {
                return false;
              }
              const yKey = getSafeYKey({
                yMetricKey: metric,
                record: history[0],
              });

              return yKey in history[0];
            }) ?? [];

          const yKey = getSafeYKey({
            yMetricKey: metric,
            record: matchedHistory[0],
          });
          // Time weighted ema is the default
          let smoothedMetric = metric + '_time_weighted_ema';
          let hasSmoothedBackendLine = false;

          if (matchedHistory.length > 0) {
            for (const suffix of [
              '_time_weighted_ema',
              '_ema',
              '_gaussian',
              '_running_average',
            ]) {
              const metricWithSuffix = metric + suffix;

              const field = yKey.replace(metric, metricWithSuffix);
              if (field in matchedHistory[0]) {
                smoothedMetric = metricWithSuffix;
                hasSmoothedBackendLine = true;
                break;
              }
            }
          }

          const groupAreaToAggType: Record<string, LineAggregation> = {
            stddev: 'stdDev',
            stderr: 'stdErr',
            minmax: 'minMax',
          };
          let agg: LineAggregation | undefined;
          if (type === 'Avg') {
            agg = 'Avg';
          } else {
            agg = groupAreaToAggType[lineConfig.groupArea];
          }

          return {
            agg,
            isAux: type !== 'Avg',
            smoothedMetric,
            hasSmoothedBackendLine:
              hasSmoothedBackendLine &&
              behavior.useBackendSmoothedLineIfPresent,
            matchedHistory,
            metric,
            metricIndex,
            runName: run.name,
            runsetId: run.runsetId,
            yKey,
          };
        });
      });
    }
  );

  const lines = lineSeeds.flatMap(function buildLineFromSeed({
    agg,
    smoothedMetric,
    hasSmoothedBackendLine,
    isAux,
    matchedHistory,
    metric,
    metricIndex,
    runName,
    runsetId,
  }) {
    if (!agg) {
      // "None" aggregation type -> don't show any min/max bounds
      return [];
    }
    const nfList = nfPointsByRunAggMetric[runName][metric];
    try {
      const run = getLineDataByKey(lineDataByKey, runsetId, runName);

      const line = convertLineTemplateToLine(run, {
        agg: agg.toLowerCase() as LineProperties['aggregation'],
        isAux,
        metric,
        metricIndex,
      });

      const ta = performance.now();

      // The purpose of this code block is to attempt to retrieve a backend-provided precomputed line for a given metric and x-axis. If such a backend line is not found, the system logs diagnostic information. This enables graceful fallback to client-side computation while ensuring missing backend support can be tracked and triaged.
      const attemptBackendLine =
        behavior.useBackendLineData && line.historyLines;
      let backendLine: BackendLine | undefined;
      if (attemptBackendLine) {
        backendLine = line.historyLines!.find(
          bl => bl.yMetric === metric && bl.xMetric === lineConfig.xAxis
        );

        if (!backendLine) {
          const yMetricMatch = line.historyLines!.find(
            bl => bl.yMetric === metric
          );
          DD_RUM.addAction('backend lines not supported', {
            reason: 'backend line missing x/y metric match',
            yMetric: metric,
            xMetric: lineConfig.xAxis,
            runId: run.run.id,
            hasYMetricMatch: !!yMetricMatch,
            xMetricForYMetricMatch: yMetricMatch?.xMetric,
            panelId,
          });
        }
      }

      if (backendLine) {
        line.data = isAux ? backendLine.areaLine : backendLine.historyLine;
        line.historyLines = [];
        if (backendLine.smoothedLine) {
          line.smoothedLineFromBackend = {
            ...line,
            smoothed: true,
            meta: {
              ...line.meta,
              category: 'smoothed',
            },
            data: backendLine.smoothedLine,
          };
        }

        // Preserve pre-calculated domain from backend
        line.domain = {
          minX: backendLine.minX,
          maxX: backendLine.maxX,
          minY: backendLine.minY,
          maxY: backendLine.maxY,
        };
        // Wire backend non-finite points to nanPoints for rendering
        // Only primary lines (non-aux) should show non-finite markers
        line.nanPoints = [];
        if (!isAux) {
          // TODO: come back and clean up this type
          // IT HAS A TICKET TO MAKE SURE IT GETS DONE: https://wandb.atlassian.net/browse/WB-29366
          //
          line.nanPoints = backendLine.nonFiniteLine as any as SafePoint[];
        }
      } else {
        const {finitePoints, nanPoints} = parseHistory(
          matchedHistory as HistoryRecord[],
          metric,
          lineConfig.xAxis,
          xMetrics.filter(x => x !== lineConfig.xAxis),
          {
            agg,
            startTime: {
              value: line.startTimeInMs!,
              unit: 'milliseconds',
            },
            usingExpressions: behavior.usingExpressions,
          },
          // we don't allow targeting min/max outside of groups currently
          isRunsetGrouped(runsetId)
            ? groupAgg
            : (BIN_POINT_TO_USE.toLowerCase() as ChartAggOption)
        );

        line.data = finitePoints;
        line.nanPoints = isAux ? [] : nfList; // aux lines won't get NFs

        const tb = performance.now();
        if (debugMode && tb - ta > 1) {
          console.log('Line creation: parse history: ', tb - ta, 'ms');
        }

        // Push all the NFs into the nanList: because this list reference is created outside the loop here we can mutate it from inside both agg line loops. I don't love this either.
        nfList.push(...nanPoints);

        if (hasSmoothedBackendLine) {
          addSmoothedLineFromBackend(line, {
            agg,
            behavior,
            smoothedMetric,
            finitePoints,
            groupAgg,
            isRunsetGrouped: isRunsetGrouped(runsetId),
            matchedHistory,
            xAxis: lineConfig.xAxis,
            xMetrics,
          });
        }
        const tc = performance.now();
        if (debugMode && tc - tb > 1) {
          console.log(
            'Line creation: add smoothed line from backend: ',
            tc - tb,
            'ms'
          );
        }
      }

      return line;
    } catch (e) {
      console.error(`Error creating line in useLines`, e);
      return [];
    }
  });

  return lines;
}
